/**
 * Helpers for building renderer-agnostic and renderer-specific threshold plot
 * settings from catalog metadata and /measures/v2 responses.
 */

import type {
  BreachItem,
  ThresholdBreachReport,
  ThresholdBreachesResult,
  ThresholdRepresentation,
  ThresholdsResult,
} from '@highimpact-frontend/api-types';
import Plotly from 'plotly.js-dist-min';
import type { UIThresholdModel } from './catalog-transform';

// -- type definitions --

export interface ThresholdVariableColumnMapping {
  thresholdScopeAssignmentId: number;
  variables: Readonly<Record<string, string>>;
}

export type ThresholdStyleSelection =
  | { mode: 'default' }
  | {
      mode: 'explicit-style-id';
      thresholdAssignmentStyleId: number;
    };

export interface DataAccessor<Row> {
  getRowAtIndex?: (index: number) => Row | undefined;
  getRowTimestamp?: (row: Row) => string | Date | number | null;
  getNumericValue?: (row: Row, column: string) => number | null;
  getRawValue?: (row: Row, column: string) => unknown;
}

export interface ThresholdPlotSettingsInput<Row> {
  thresholds: UIThresholdModel[];
  thresholdRepresentations?:
    | ThresholdsResult
    | ThresholdRepresentation[]
    | null;
  variableMappings: ThresholdVariableColumnMapping[];
  thresholdBreaches: ThresholdBreachesResult | null | undefined;
  measuredRows: Row[];
  dataAccessor?: DataAccessor<Row>;
  /** Offset applied to API breach `row_index` when rows are merged with a detail batch. */
  rowIndexOffset?: number;
  styleSelectionByThresholdId?: Record<number, ThresholdStyleSelection>;
  markerBorderWidthPx?: number;
}

export interface ThresholdResolvedPlotStyle {
  color: string;
  dash: 'dash' | 'dot' | 'solid' | 'longdash' | 'dashdot' | null;
  width: number;
  fillColor: string | null;
  opacity: number | null;
}

export interface ThresholdOverlaySeriesPoint {
  x: string | number | Date | null;
  y: number | null;
}

export interface ThresholdOverlaySeriesModel {
  thresholdScopeAssignmentId: number;
  thresholdId: number;
  label: string;
  geometryIndex: number;
  curveColumn: string | null;
  style: ThresholdResolvedPlotStyle;
  representationCoordinateSystem: string;
  representationMode: string;
  points: ThresholdOverlaySeriesPoint[];
}

export interface BreachPointMatch {
  thresholdScopeAssignmentId: number;
  rowIndex: number;
  breachIndexInReport: number;
  reason?: string;
  thresholdValue?: number;
  delta?: number;
  signedDelta?: number | null;
}

export interface SensorSeriesEnrichment {
  thresholdScopeAssignmentId: number;
  curveColumn: string;
  markerBorderColorByRowIndex: Record<number, string>;
  markerBorderWidthByRowIndex: Record<number, number>;
  tooltipByRowIndex: Record<number, string[]>;
}

export interface ThresholdPlotSettingsCoreOutput {
  overlaySeries: ThresholdOverlaySeriesModel[];
  sensorEnrichmentByThreshold: SensorSeriesEnrichment[];
  matchedBreaches: BreachPointMatch[];
  skippedThresholdIds: number[];
  diagnostics: string[];
}

export interface PlotlySensorTracePatch {
  marker?: {
    line?: {
      color?: (string | undefined)[];
      width?: (number | undefined)[];
    };
  };
  customdataAppend?: unknown[][];
  hovertemplateAugment?: string;
}

export interface PlotlyThresholdSettings {
  thresholdTraces: Partial<Plotly.PlotData>[];
  sensorTracePatchesByCurveId: Record<string, PlotlySensorTracePatch>;
}

export interface EChartsThresholdSettings {
  thresholdSeries: unknown[];
  sensorSeriesPatchesById: Record<string, unknown>;
}

// -- constants --

const DEFAULT_THRESHOLD_COLOR = '#d62728';
const DEFAULT_THRESHOLD_WIDTH = 2;
const DEFAULT_MARKER_BORDER_WIDTH = 2.5;
const DEFAULT_HOVER_AUGMENT =
  '<br><b>Dépassement</b>: %{customdata[0]}<br>Raison: %{customdata[1]}<br>Valeur de seuil: %{customdata[2]}<br>Delta signé: %{customdata[3]}';

function deduplicateOverlaySeriesForPlotly(
  overlaySeries: readonly ThresholdOverlaySeriesModel[]
): ThresholdOverlaySeriesModel[] {
  const uniqueSeriesByThresholdAndGeometry = new Map<
    string,
    ThresholdOverlaySeriesModel
  >();

  overlaySeries.forEach((series) => {
    const dedupeKey = `${series.thresholdId}:${series.geometryIndex}`;
    if (!uniqueSeriesByThresholdAndGeometry.has(dedupeKey)) {
      uniqueSeriesByThresholdAndGeometry.set(dedupeKey, series);
    }
  });

  return [...uniqueSeriesByThresholdAndGeometry.values()];
}

// -- functions --

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function normalizeKey(value: string): string {
  return value.trim().toLowerCase();
}

function normalizeTimestamp(value: unknown): string | null {
  if (typeof value === 'string') {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toISOString();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return new Date(value).toISOString();
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  return null;
}

function instantToEpochMs(value: unknown): number | null {
  if (value == null) {
    return null;
  }

  if (value instanceof Date) {
    const epochMs = value.getTime();
    return Number.isFinite(epochMs) ? epochMs : null;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function toFiniteNumberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function createDefaultAccessor<Row>(
  rows: readonly Row[]
): Required<DataAccessor<Row>> {
  return {
    getRowAtIndex: (index: number) => rows[index],
    getRowTimestamp: (row: Row) => {
      if (!isRecord(row)) {
        return null;
      }

      const resolved =
        row.Temps ?? row.temps ?? row.timestamp ?? row.time ?? null;
      return normalizeTimestamp(resolved);
    },
    getNumericValue: (row: Row, column: string) => {
      if (!isRecord(row)) {
        return null;
      }

      return toFiniteNumberOrNull(row[column]);
    },
    getRawValue: (row: Row, column: string) => {
      if (!isRecord(row)) {
        return undefined;
      }

      return row[column];
    },
  };
}

function resolveAccessor<Row>(
  rows: readonly Row[],
  accessor?: DataAccessor<Row>
): Required<DataAccessor<Row>> {
  const fallback = createDefaultAccessor(rows);

  return {
    getRowAtIndex: accessor?.getRowAtIndex ?? fallback.getRowAtIndex,
    getRowTimestamp: accessor?.getRowTimestamp ?? fallback.getRowTimestamp,
    getNumericValue: accessor?.getNumericValue ?? fallback.getNumericValue,
    getRawValue: accessor?.getRawValue ?? fallback.getRawValue,
  };
}

function getThresholdByAssignmentId(
  thresholds: readonly UIThresholdModel[]
): Map<number, UIThresholdModel> {
  return new Map(thresholds.map((threshold) => [threshold.id, threshold]));
}

function getVariableMappingByAssignmentId(
  mappings: readonly ThresholdVariableColumnMapping[]
): Map<number, ThresholdVariableColumnMapping> {
  return new Map(
    mappings.map((mapping) => [mapping.thresholdScopeAssignmentId, mapping])
  );
}

function normalizeRepresentations(
  representations?: ThresholdsResult | readonly ThresholdRepresentation[] | null
): readonly ThresholdRepresentation[] {
  if (!representations) {
    return [];
  }

  return Array.isArray(representations)
    ? representations
    : 'items' in representations
    ? representations.items ?? []
    : [];
}

function getDependentVariableName(threshold: UIThresholdModel): string | null {
  const dependentVariable = threshold.variableDetails.find(
    (variable) => variable.role === 'dependent'
  );

  if (dependentVariable) {
    return dependentVariable.variableName;
  }

  const fallback = threshold.variables.find((variableName) => {
    const normalized = normalizeKey(variableName);
    return normalized === 'value' || normalized === 'y';
  });

  return fallback ?? threshold.variables[0] ?? null;
}

function getCurveColumn(
  threshold: UIThresholdModel,
  mapping: ThresholdVariableColumnMapping | undefined
): string | null {
  if (!mapping) {
    return null;
  }

  const dependentVariableName = getDependentVariableName(threshold);

  if (!dependentVariableName) {
    return Object.values(mapping.variables)[0] ?? null;
  }

  return mapping.variables[dependentVariableName] ?? null;
}

function resolveStyle(
  threshold: UIThresholdModel,
  styleSelection?: ThresholdStyleSelection
): ThresholdResolvedPlotStyle {
  const explicitStyle =
    styleSelection?.mode === 'explicit-style-id'
      ? threshold.availableStyles.find(
          (style) => style.id === styleSelection.thresholdAssignmentStyleId
        )
      : undefined;
  const resolvedStyle = explicitStyle ?? threshold.defaultStyle;

  return {
    color: resolvedStyle?.color ?? DEFAULT_THRESHOLD_COLOR,
    dash: resolvedStyle?.dash ?? null,
    width: resolvedStyle?.width ?? DEFAULT_THRESHOLD_WIDTH,
    fillColor: resolvedStyle?.fillColor ?? null,
    opacity: resolvedStyle?.opacity ?? null,
  };
}

function buildOverlaySeries(
  threshold: UIThresholdModel,
  representation: ThresholdRepresentation,
  style: ThresholdResolvedPlotStyle,
  curveColumn: string | null,
  diagnostics: string[]
): ThresholdOverlaySeriesModel[] {
  if (representation.representation_coordinate_system !== 'time_series') {
    diagnostics.push(
      `Threshold ${threshold.id} skipped for overlay: unsupported representation coordinate system ${representation.representation_coordinate_system}`
    );
    return [];
  }

  return representation.geometries.flatMap((geometry, geometryIndex) => {
    if (
      geometry.kind !== 'time_series' ||
      geometry.payload.type !== 'time_series'
    ) {
      diagnostics.push(
        `Threshold ${threshold.id} geometry ${geometryIndex} skipped: unsupported geometry kind ${geometry.kind}`
      );
      return [];
    }

    return [
      {
        thresholdScopeAssignmentId:
          representation.threshold_scope_assignment_id,
        thresholdId: representation.threshold_id,
        label:
          representation.geometries.length > 1
            ? `${representation.name} (${geometryIndex + 1})`
            : representation.name,
        geometryIndex,
        curveColumn,
        style,
        representationCoordinateSystem:
          representation.representation_coordinate_system,
        representationMode: threshold.representationMode,
        points: geometry.payload.points.map(([x, y]) => ({ x, y })),
      },
    ];
  });
}

function isCoordinateMatch<Row>(
  breach: BreachItem,
  row: Row,
  threshold: UIThresholdModel,
  mapping: ThresholdVariableColumnMapping,
  accessor: Required<DataAccessor<Row>>
): boolean {
  if (!isRecord(breach.coordinates)) {
    return false;
  }

  const coordinateEntries = Object.entries(breach.coordinates);

  if (coordinateEntries.length === 0) {
    return false;
  }

  return coordinateEntries.every(([variableName, coordinateValue]) => {
    const column = mapping.variables[variableName];

    if (!column) {
      return false;
    }

    const rowValue = accessor.getRawValue(row, column);
    const rowTimestamp = accessor.getRowTimestamp(row);
    const normalizedVariableName = normalizeKey(variableName);
    const isTimeVariable =
      threshold.variableDetails.some(
        (variable) =>
          variable.variableName === variableName && variable.role === 'time'
      ) ||
      normalizedVariableName === 't' ||
      normalizedVariableName === 'time';

    if (isTimeVariable) {
      return (
        normalizeTimestamp(rowValue ?? rowTimestamp) ===
        normalizeTimestamp(coordinateValue)
      );
    }

    return rowValue === coordinateValue;
  });
}

function findMatchingRowIndex<Row>(
  breach: BreachItem,
  threshold: UIThresholdModel,
  mapping: ThresholdVariableColumnMapping | undefined,
  rows: readonly Row[],
  accessor: Required<DataAccessor<Row>>,
  diagnostics: string[],
  rowIndexOffset = 0
): number | null {
  if (Number.isInteger(breach.row_index)) {
    const adjustedIndex = breach.row_index + rowIndexOffset;

    if (adjustedIndex >= 0 && adjustedIndex < rows.length) {
      return adjustedIndex;
    }
  }

  if (!mapping) {
    diagnostics.push(
      `Threshold ${threshold.id} breach could not be matched: missing variable mapping`
    );
    return null;
  }

  const fallbackIndex = rows.findIndex((row) =>
    isCoordinateMatch(breach, row, threshold, mapping, accessor)
  );

  if (fallbackIndex === -1) {
    diagnostics.push(
      `Threshold ${threshold.id} breach could not be matched to a measured row`
    );
    return null;
  }

  return fallbackIndex;
}

function normalizeReason(reason: string): string {
  return reason.replace(/^reason\s*:\s*/i, '').trim();
}

function formatNumberForTooltip(value: unknown): string {
  const numericValue = toFiniteNumberOrNull(value);
  return numericValue == null ? '-' : numericValue.toFixed(2);
}

function formatSignedNumberForTooltip(value: unknown): string {
  const numericValue = toFiniteNumberOrNull(value);

  if (numericValue == null) {
    return '-';
  }

  const signPrefix = numericValue > 0 ? '+' : '';
  return `${signPrefix}${numericValue.toFixed(2)}`;
}

function getSignedDeltaForBreach(
  threshold: UIThresholdModel,
  breach: BreachItem
): number | null {
  if (
    typeof breach.signed_delta === 'number' &&
    Number.isFinite(breach.signed_delta)
  ) {
    return breach.signed_delta;
  }

  const absoluteDelta = toFiniteNumberOrNull(breach.delta);

  if (absoluteDelta == null) {
    return null;
  }

  return threshold.boundKind === 'maximum' ? absoluteDelta : -absoluteDelta;
}

function resolveTooltipReason(
  reportReason: string,
  threshold: UIThresholdModel,
  signedDeltaLabel: string
): string {
  const normalizedReason = normalizeReason(reportReason);

  if (normalizedReason.toLowerCase() !== 'evaluated') {
    return normalizedReason;
  }

  const boundLabel = threshold.boundKind === 'maximum' ? 'haut' : 'bas';
  return `dépassement par le ${boundLabel} (${signedDeltaLabel})`;
}

function formatTooltipValues(
  threshold: UIThresholdModel,
  report: ThresholdBreachReport,
  breach: BreachItem
): string[] {
  const signedDelta = getSignedDeltaForBreach(threshold, breach);
  const signedDeltaLabel = formatSignedNumberForTooltip(signedDelta);

  return [
    threshold.label,
    resolveTooltipReason(report.reason, threshold, signedDeltaLabel),
    formatNumberForTooltip(breach.threshold_value),
    signedDeltaLabel,
  ];
}

/**
 * Builds renderer-agnostic threshold overlay series and sensor enrichment data
 * from threshold catalog metadata and the threshold-related fragments returned
 * by `/measures/v2`.
 */
export function buildThresholdPlotSettingsCore<Row>(
  input: ThresholdPlotSettingsInput<Row>
): ThresholdPlotSettingsCoreOutput {
  const diagnostics: string[] = [];
  const thresholdByAssignmentId = getThresholdByAssignmentId(input.thresholds);
  const mappingByAssignmentId = getVariableMappingByAssignmentId(
    input.variableMappings
  );
  const accessor = resolveAccessor(input.measuredRows, input.dataAccessor);
  const matchedBreaches: BreachPointMatch[] = [];
  const sensorEnrichmentByThreshold: SensorSeriesEnrichment[] = [];
  const overlaySeries: ThresholdOverlaySeriesModel[] = [];
  const skippedThresholdIds = new Set<number>();
  const markerBorderWidth =
    input.markerBorderWidthPx ?? DEFAULT_MARKER_BORDER_WIDTH;

  normalizeRepresentations(input.thresholdRepresentations).forEach(
    (representation) => {
      const threshold = thresholdByAssignmentId.get(
        representation.threshold_scope_assignment_id
      );

      if (!threshold) {
        skippedThresholdIds.add(representation.threshold_scope_assignment_id);
        diagnostics.push(
          `Threshold representation ${representation.threshold_scope_assignment_id} skipped: catalog metadata missing`
        );
        return;
      }

      const mapping = mappingByAssignmentId.get(threshold.id);
      const curveColumn = getCurveColumn(threshold, mapping);
      const style = resolveStyle(
        threshold,
        input.styleSelectionByThresholdId?.[threshold.id]
      );

      const series = buildOverlaySeries(
        threshold,
        representation,
        style,
        curveColumn,
        diagnostics
      );

      if (series.length === 0) {
        skippedThresholdIds.add(threshold.id);
      }

      overlaySeries.push(...series);
    }
  );

  (input.thresholdBreaches?.items ?? []).forEach((report) => {
    const threshold = thresholdByAssignmentId.get(
      report.threshold_scope_assignment_id
    );

    if (!threshold) {
      skippedThresholdIds.add(report.threshold_scope_assignment_id);
      diagnostics.push(
        `Threshold breaches ${report.threshold_scope_assignment_id} skipped: catalog metadata missing`
      );
      return;
    }

    const mapping = mappingByAssignmentId.get(threshold.id);
    const curveColumn = getCurveColumn(threshold, mapping);

    if (!curveColumn) {
      skippedThresholdIds.add(threshold.id);
      diagnostics.push(
        `Threshold ${threshold.id} breach enrichment skipped: dependent curve column could not be resolved`
      );
      return;
    }

    const style = resolveStyle(
      threshold,
      input.styleSelectionByThresholdId?.[threshold.id]
    );

    const markerBorderColorByRowIndex: Record<number, string> = {};
    const markerBorderWidthByRowIndex: Record<number, number> = {};
    const tooltipByRowIndex: Record<number, string[]> = {};

    report.breaches.forEach((breach, breachIndex) => {
      if (!breach.breached) {
        return;
      }

      const rowIndex = findMatchingRowIndex(
        breach,
        threshold,
        mapping,
        input.measuredRows,
        accessor,
        diagnostics,
        input.rowIndexOffset ?? 0
      );

      if (rowIndex == null) {
        return;
      }

      markerBorderColorByRowIndex[rowIndex] = style.color;
      markerBorderWidthByRowIndex[rowIndex] = markerBorderWidth;
      const signedDelta = getSignedDeltaForBreach(threshold, breach);
      tooltipByRowIndex[rowIndex] = [
        ...(tooltipByRowIndex[rowIndex] ?? []),
        ...formatTooltipValues(threshold, report, breach),
      ];
      matchedBreaches.push({
        thresholdScopeAssignmentId: threshold.id,
        rowIndex,
        breachIndexInReport: breachIndex,
        reason: report.reason,
        thresholdValue: breach.threshold_value,
        delta: breach.delta,
        signedDelta,
      });
    });

    sensorEnrichmentByThreshold.push({
      thresholdScopeAssignmentId: threshold.id,
      curveColumn,
      markerBorderColorByRowIndex,
      markerBorderWidthByRowIndex,
      tooltipByRowIndex,
    });
  });

  (input.thresholdBreaches?.not_evaluable ?? []).forEach((item) => {
    diagnostics.push(
      `Threshold ${item.threshold_scope_assignment_id} not evaluable: ${item.reason}`
    );
    skippedThresholdIds.add(item.threshold_scope_assignment_id);
  });

  (input.thresholdRepresentations &&
  !Array.isArray(input.thresholdRepresentations)
    ? 'not_representable' in input.thresholdRepresentations
      ? input.thresholdRepresentations.not_representable ?? []
      : []
    : []
  ).forEach((item) => {
    diagnostics.push(
      `Threshold ${item.threshold_scope_assignment_id} not representable: ${item.reason}`
    );
    skippedThresholdIds.add(item.threshold_scope_assignment_id);
  });

  return {
    overlaySeries,
    sensorEnrichmentByThreshold,
    matchedBreaches,
    skippedThresholdIds: [...skippedThresholdIds],
    diagnostics,
  };
}

/**
 * Adapts threshold plot settings to Plotly traces and per-sensor trace patches.
 */
export function adaptThresholdSettingsToPlotly(
  core: ThresholdPlotSettingsCoreOutput,
  ctx: {
    readonly curveIdByColumn: Readonly<Record<string, string>>;
    readonly xValuesByRowIndex?: readonly unknown[];
  }
): PlotlyThresholdSettings {
  const thresholdTraces: Partial<Plotly.PlotData>[] =
    deduplicateOverlaySeriesForPlotly(core.overlaySeries).map((series) => ({
      type: 'scatter',
      mode: 'lines',
      // Epoch-ms x matches sensor timeseries traces; labels use displayTimezone in scatterplot.
      x: series.points.map((point) => instantToEpochMs(point.x)),
      y: series.points.map((point) => point.y),
      name: series.label,
      line: {
        color: series.style.color,
        dash: series.style.dash ?? 'solid',
        width: series.style.width,
      },
      opacity: series.style.opacity ?? undefined,
      fillcolor: series.style.fillColor ?? undefined,
      hovertemplate: '<b>%{fullData.name}</b>: %{y}<extra></extra>',
      meta: {
        thresholdScopeAssignmentId: series.thresholdScopeAssignmentId,
        thresholdId: series.thresholdId,
      },
    }));

  const sensorTracePatchesByCurveId: Record<string, PlotlySensorTracePatch> =
    {};

  core.sensorEnrichmentByThreshold.forEach((enrichment) => {
    const curveId = ctx.curveIdByColumn[enrichment.curveColumn];

    if (!curveId) {
      return;
    }

    const existingPatch = sensorTracePatchesByCurveId[curveId];
    const currentLength = Math.max(
      ctx.xValuesByRowIndex?.length ?? 0,
      ...Object.keys(enrichment.markerBorderColorByRowIndex).map(
        (key) => Number(key) + 1
      ),
      ...Object.keys(enrichment.tooltipByRowIndex).map(
        (key) => Number(key) + 1
      ),
      0
    );
    const colorArray = [
      ...(existingPatch?.marker?.line?.color ??
        Array(currentLength).fill(undefined)),
    ];
    const widthArray = [
      ...(existingPatch?.marker?.line?.width ??
        Array(currentLength).fill(undefined)),
    ];
    const customdataAppend = [
      ...(existingPatch?.customdataAppend ??
        Array(currentLength)
          .fill(undefined)
          .map(() => [])),
    ] as unknown[][];

    Object.entries(enrichment.markerBorderColorByRowIndex).forEach(
      ([rowIndexString, color]) => {
        colorArray[Number(rowIndexString)] = color;
      }
    );
    Object.entries(enrichment.markerBorderWidthByRowIndex).forEach(
      ([rowIndexString, width]) => {
        widthArray[Number(rowIndexString)] = width;
      }
    );
    Object.entries(enrichment.tooltipByRowIndex).forEach(
      ([rowIndexString, lines]) => {
        customdataAppend[Number(rowIndexString)] = lines;
      }
    );

    sensorTracePatchesByCurveId[curveId] = {
      marker: {
        line: {
          color: colorArray,
          width: widthArray,
        },
      },
      customdataAppend,
      hovertemplateAugment: DEFAULT_HOVER_AUGMENT,
    };
  });

  return {
    thresholdTraces,
    sensorTracePatchesByCurveId,
  };
}

/**
 * Adapts threshold plot settings to an ECharts-friendly shape.
 */
export function adaptThresholdSettingsToECharts(
  core: ThresholdPlotSettingsCoreOutput,
  ctx: {
    readonly seriesIdByColumn: Readonly<Record<string, string>>;
  }
): EChartsThresholdSettings {
  const thresholdSeries = core.overlaySeries.map((series) => ({
    id: `threshold:${series.thresholdScopeAssignmentId}:${series.geometryIndex}`,
    name: series.label,
    type: 'line',
    showSymbol: false,
    lineStyle: {
      color: series.style.color,
      type:
        series.style.dash === 'dash'
          ? 'dashed'
          : series.style.dash === 'dot'
          ? 'dotted'
          : 'solid',
      width: series.style.width,
      opacity: series.style.opacity ?? undefined,
    },
    areaStyle: series.style.fillColor
      ? {
          color: series.style.fillColor,
          opacity: series.style.opacity ?? undefined,
        }
      : undefined,
    data: series.points.map((point) => [point.x, point.y]),
  }));

  const sensorSeriesPatchesById: Record<string, unknown> = {};

  core.sensorEnrichmentByThreshold.forEach((enrichment) => {
    const seriesId = ctx.seriesIdByColumn[enrichment.curveColumn];

    if (!seriesId) {
      return;
    }

    sensorSeriesPatchesById[seriesId] = {
      emphasis: { focus: 'series' },
      thresholdEnrichment: {
        markerBorderColorByRowIndex: enrichment.markerBorderColorByRowIndex,
        markerBorderWidthByRowIndex: enrichment.markerBorderWidthByRowIndex,
        tooltipByRowIndex: enrichment.tooltipByRowIndex,
      },
    };
  });

  return {
    thresholdSeries,
    sensorSeriesPatchesById,
  };
}
